import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import HeroSection from "./components/HeroSection";
import CategoryProductBrowser from "./components/CategoryProductBrowser";
import EventPopup from "./components/EventPopup";
import SidebarCompanyScroller from "./components/SidebarCompanyScroller";
import AssociationEventsSection from "./components/AssociationEventsSection";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "@/components/SEO";
import { OrganizationSchema, FAQSchema } from "@/components/StructuredData";

const HomePage = () => {
  useTranslation();
  const isKr = false;

  const faqs = [
    {
      question: "KCANIA là gì và hoạt động như thế nào?",
      answer: "KCANIA (Korea Carbon and Nano Industry Association) là hiệp hội ngành công nghiệp Carbon và Nano Hàn Quốc. Tại Việt Nam, chúng tôi vận hành nền tảng B2B giúp kết nối các doanh nghiệp Hàn Quốc với đối tác Việt Nam, cung cấp thông tin sản phẩm, hỗ trợ hợp tác kỹ thuật và tổ chức sự kiện giao thương.",
    },
    {
      question: "Làm thế nào để tìm kiếm sản phẩm công nghệ từ doanh nghiệp Hàn Quốc?",
      answer: "Bạn có thể sử dụng thanh tìm kiếm tại trang chủ hoặc truy cập mục 'Danh Mục' để duyệt sản phẩm theo ngành nghề. Mỗi sản phẩm đều có thông tin chi tiết từ nhà sản xuất Hàn Quốc bằng tiếng Việt.",
    },
    {
      question: "Tôi có thể liên hệ trực tiếp với doanh nghiệp Hàn Quốc không?",
      answer: "Có. Qua nền tảng KCANIA, bạn có thể gửi yêu cầu tư vấn trực tiếp cho từng sản phẩm. Đội ngũ Bizwell (hỗ trợ phiên dịch Hàn–Việt) sẽ kết nối bạn với doanh nghiệp.",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <SEO
        title="KCANIA – Kết Nối Doanh Nghiệp Công Nghệ Carbon Nano Hàn Quốc"
        description="KCANIA – Nền tảng B2B kết nối sản phẩm công nghệ Carbon & Nano Hàn Quốc với doanh nghiệp Việt Nam. Khám phá 200+ doanh nghiệp Hàn Quốc, hàng nghìn sản phẩm công nghệ tiên tiến."
        keywords="KCANIA, sản phẩm Hàn Quốc, Carbon Nano, B2B Việt Nam, doanh nghiệp Hàn Quốc, hợp tác kỹ thuật, hiệp hội Carbon Nano"
        ogType="website"
      />
      <OrganizationSchema type="Corporation" />
      <FAQSchema faqs={faqs} />

      <Navbar />
      <EventPopup isKr={isKr} />

      <main id="main-content">
        {/* Hero Banner */}
        <section aria-label="Giới thiệu KCANIA – Kết nối doanh nghiệp Hàn–Việt">
          <HeroSection />
        </section>

        {/* 2-column layout: Main Content | Right Sidebar */}
        <div className="flex items-stretch relative">

          {/* MAIN CONTENT — takes all remaining width */}
          <div className="flex-1 min-w-0">
            <section aria-label="Danh mục sản phẩm công nghệ Hàn Quốc">
              <CategoryProductBrowser />
            </section>
          </div>

          {/* RIGHT SIDEBAR — Company Logo Scroller (Desktop only) */}
          <aside
            className="hidden lg:block flex-shrink-0 w-[230px]"
            aria-label="Doanh nghiệp Hàn Quốc nổi bật"
          >
            <div className="sticky top-[72px] h-[calc(100vh-72px)] w-[230px]">
              <SidebarCompanyScroller isKr={isKr} />
            </div>
          </aside>
        </div>

        {/* Association + Events + Support Programs */}
        <section aria-label="Hoạt động hiệp hội, sự kiện và chương trình hỗ trợ">
          <AssociationEventsSection />
        </section>

        {/* CTA Banner */}
        <section className="py-10 bg-[#004A99] relative overflow-hidden" aria-label="Kết nối với doanh nghiệp Hàn Quốc">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://readdy.ai/api/search-image?query=abstract%20geometric%20pattern%20Korean%20traditional%20architecture%20clean%20modern%20minimal%20light%20texture%20background%20navy%20blue&width=1920&height=400&seq=cta_bg_primary&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-top"
              aria-hidden="true"
            />
          </div>
          <div className="relative z-10 max-w-[860px] mx-auto px-6 text-center">
            <span className="inline-block bg-white/20 text-white text-xs px-3 py-1 rounded-full mb-3 font-medium">
              🇰🇷 × 🇻🇳 Kết Nối Doanh Nghiệp Hàn–Việt
            </span>
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3 leading-tight">
              Kết Nối Với Doanh Nghiệp Hàn Quốc Ngày Hôm Nay
            </h2>
            <p className="text-white/80 text-sm mb-5 max-w-lg mx-auto">
              Khám phá sản phẩm và công nghệ mới nhất từ các doanh nghiệp Hàn Quốc.
            </p>
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <Link
                to="/danh-muc"
                className="inline-flex items-center gap-2 bg-white text-[#004A99] hover:bg-gray-50 px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
                rel="noopener"
              >
                <i className="ri-search-line" aria-hidden="true" />
                Tìm Kiếm Sản Phẩm
              </Link>
              <Link
                to="/doanh-nghiep"
                className="inline-flex items-center gap-2 border-2 border-white/50 text-white hover:bg-white/10 px-6 py-2.5 rounded-xl font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-building-2-line" aria-hidden="true" />
                Xem Doanh Nghiệp
              </Link>
            </div>
            <div className="flex items-center justify-center gap-5 mt-5 flex-wrap" role="list" aria-label="Lợi ích KCANIA">
              {[
                { icon: "ri-translate-2", label: "Nội dung tiếng Việt" },
                { icon: "ri-shield-check-line", label: "Doanh nghiệp được xác minh" },
                { icon: "ri-customer-service-2-line", label: "Hỗ trợ song ngữ" },
                { icon: "ri-links-line", label: "Kết nối trực tiếp" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-1.5 text-white/80 text-xs" role="listitem">
                  <i className={`${item.icon} text-white text-sm`} aria-hidden="true" />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section (AEO/GEO — hidden visually but helps AI readability) */}
        <section
          className="max-w-[860px] mx-auto px-6 py-12"
          aria-label="Câu hỏi thường gặp về KCANIA"
          itemScope
          itemType="https://schema.org/FAQPage"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Câu Hỏi Thường Gặp
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <details
                key={i}
                className="bg-white rounded-xl border border-gray-100 p-5 cursor-pointer group"
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
              >
                <summary className="font-semibold text-gray-900 text-sm list-none flex items-center justify-between">
                  <span itemProp="name">{faq.question}</span>
                  <i className="ri-arrow-down-s-line text-gray-400 group-open:rotate-180 transition-transform" aria-hidden="true" />
                </summary>
                <div
                  className="mt-3 text-sm text-gray-600 leading-relaxed"
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <p itemProp="text">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Còn thắc mắc?{" "}
              <a
                href="https://zalo.me/0965180170"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#004A99] font-semibold hover:underline"
              >
                Liên hệ tư vấn
              </a>{" "}
              hoặc{" "}
              <Link to="/tra-cuu-tu-van" className="text-[#004A99] font-semibold hover:underline">
                tra cứu yêu cầu tư vấn
              </Link>.
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default HomePage;